import { useEffect, useState } from "react";
import { ArrowUp, CloudRain, Waves, Wind } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Loading, PageHeader, Panel } from "../components/common";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const LAT = 41.7374, LON = 12.2448;
const windy = (o) => `https://embed.windy.com/embed2.html?lat=${LAT}&lon=${LON}&detailLat=${LAT}&detailLon=${LON}&width=650&height=460&zoom=9&level=surface&overlay=${o}&product=ecmwf&menu=&message=true&marker=true&calendar=now&pressure=&type=map&location=coordinates&detail=true&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1`;
const DIRS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSO", "SO", "OSO", "O", "ONO", "NO", "NNO"];
const dir = (deg) => DIRS[Math.round(((deg ?? 0) % 360) / 22.5) % 16];
const GIORNI = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const dayLabel = (iso, i) => (i === 0 ? "Oggi" : i === 1 ? "Domani" : GIORNI[new Date(`${iso}T00:00:00`).getDay()]);
const Arrow = ({ deg }) => <ArrowUp className="w-4 h-4 inline" style={{ transform: `rotate(${(deg ?? 0) + 180}deg)` }} />;

function AlertPrefs() {
  const [p, setP] = useState(null);
  useEffect(() => { api("/weather/alert-prefs").then(setP).catch(() => setP({ enabled: false, wind_kn: 25, wave_m: 1.5 })); }, []);
  if (!p) return null;
  const save = async (next) => {
    try { await api("/weather/alert-prefs", { method: "PUT", body: next }); setP(next); toast.success(next.enabled && !p.enabled ? "Avvisi meteo attivati" : "Avvisi meteo aggiornati"); } catch { toast.error("Errore nel salvataggio"); }
  };
  return (
    <Panel className="space-y-4" data-testid="weather-alert-prefs">
      <div className="flex items-center justify-between"><div><h2 className="font-display font-bold text-[var(--navy)]">Avvisi meteo</h2><p className="text-sm text-slate-500">Notifica se le previsioni (3 giorni) superano le soglie</p></div>
        <Switch checked={!!p.enabled} onCheckedChange={(v) => save({ ...p, enabled: v })} data-testid="weather-alert-switch" /></div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">Vento oltre (kn)<Input type="number" step="1" value={p.wind_kn} onChange={(e) => setP({ ...p, wind_kn: Number(e.target.value) })} data-testid="weather-wind-input" /></label>
        <label className="text-sm">Onde oltre (m)<Input type="number" step="0.1" value={p.wave_m} onChange={(e) => setP({ ...p, wave_m: Number(e.target.value) })} data-testid="weather-wave-input" /></label>
      </div>
      <Button variant="outline" className="rounded-full" onClick={() => save(p)} data-testid="weather-save-button">Salva soglie</Button>
    </Panel>
  );
}

export default function Meteo() {
  const [w, setW] = useState(undefined);
  const [overlay, setOverlay] = useState("wind");
  useEffect(() => { api("/weather").then(setW).catch(() => setW(null)); }, []);
  return (
    <div>
      <PageHeader subtitle="Mappa & grafico Windy" title="Meteo Porto" />
      <div className="flex gap-2 mb-3">{[["wind", "Vento"], ["waves", "Onde"], ["rain", "Pioggia"]].map(([k, l]) => (
        <button key={k} onClick={() => setOverlay(k)} className={`px-4 py-1.5 rounded-full text-sm border ${overlay === k ? "bg-[var(--navy)] text-white" : "bg-white"}`} data-testid={`windy-overlay-${k}`}>{l}</button>))}</div>
      <div className="rounded-2xl overflow-hidden border h-[480px] mb-8 bg-white"><iframe key={overlay} src={windy(overlay)} title="Windy" className="w-full h-full border-0" data-testid="windy-iframe" /></div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div>
          <h2 className="font-display text-xl font-bold text-[var(--navy)] mb-1">Previsione a 3 giorni</h2><p className="text-xs text-slate-500 mb-4">Aggiornamento ogni 3 ore</p>
          {w === undefined ? <Loading /> : !w && <Panel data-testid="weather-unavailable"><p className="text-slate-600">Previsione a 3 giorni non disponibile al momento. La mappa Windy qui sopra resta consultabile.</p></Panel>}
          <div className="grid sm:grid-cols-3 gap-3" data-testid="weather-daily">{(w?.daily || []).map((d, i) => (
            <Panel key={d.date} className="!p-4"><p className="font-semibold text-[var(--navy)]">{dayLabel(d.date, i)}</p>
              <p className="text-sm mt-2"><Wind className="w-4 h-4 inline mr-1 text-[var(--teal)]" />{d.wind_speed_max_knots} kn <Arrow deg={d.wind_direction_dominant_deg} /> {dir(d.wind_direction_dominant_deg)}</p>
              <p className="text-sm"><Waves className="w-4 h-4 inline mr-1 text-[var(--teal)]" />{d.wave_height_max_m} m</p>
              <p className="text-sm"><CloudRain className="w-4 h-4 inline mr-1 text-[var(--teal)]" />{d.rain_mm} mm</p></Panel>))}</div>
        </div>
        <AlertPrefs />
      </div>
    </div>
  );
}
